type ApiErrorPayload = {
  message?: string;
  errors?: Record<string, string[] | string>;
};

type AxiosLikeError = {
  response?: {
    data?: ApiErrorPayload;
  };
};

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return Boolean(error && typeof error === "object" && "response" in error);
}

function readFieldMessages(
  errors: Record<string, string[] | string>
): string[] {
  return Object.values(errors).flatMap((value) => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }

    return typeof value === "string" ? [value] : [];
  });
}

export function resolveApiErrorMessage(error: unknown, fallback: string): string {
  if (!isAxiosLikeError(error)) {
    return fallback;
  }

  const data = error.response?.data;

  // Prefer Laravel validation bag over the generic "The given data was invalid."
  if (data?.errors) {
    const fieldMessages = readFieldMessages(data.errors);
    if (fieldMessages[0]) {
      return fieldMessages[0];
    }
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return fallback;
}

export function resolveApiFieldErrors(
  error: unknown
): Record<string, string> {
  if (!isAxiosLikeError(error)) {
    return {};
  }

  const data = error.response?.data;
  if (!data?.errors) {
    return {};
  }

  return Object.entries(data.errors).reduce<Record<string, string>>(
    (acc, [field, value]) => {
      const message = Array.isArray(value) ? value[0] : value;
      if (typeof message === "string" && message.trim()) {
        acc[field] = message;
      }
      return acc;
    },
    {}
  );
}
